import { NavLink } from 'react-router-dom';
import { ListChecks } from 'lucide-react';
import { cn } from '@hrms/ui';
import { canAnywhere, type SetupStepKey } from '@hrms/shared';
import { tsr } from '../../lib/api.js';
import { useAuthenticatedSession } from '../../session/SessionProvider.js';
import { coreSteps } from './setupSteps.js';

/** The query key every setup-status reader shares (Home, the Setup page, the welcome redirect). */
const SETUP_STATUS_QUERY_KEY = ['setup-status'];

export interface CoreSetupProgress {
  done: number;
  total: number;
  /** The counted steps, in order. */
  steps: SetupStepKey[];
}

/**
 * How far the company is through its core setup steps, for someone who may change the
 * organisation's settings. Null while loading, on an error, or for anyone else.
 */
export function useCoreSetupProgress(): CoreSetupProgress | null {
  const { actor } = useAuthenticatedSession();
  const allowed = canAnywhere(actor, 'org.settings:update');
  const status = tsr.setupStatus.getSetupStatus.useQuery({
    queryKey: SETUP_STATUS_QUERY_KEY,
    enabled: allowed,
  });
  if (!allowed || status.data?.status !== 200) return null;
  const body = status.data.body;
  const applicable = new Set<SetupStepKey>(
    body.steps.filter((step) => step.applicable).map((step) => step.key),
  );
  const doneKeys = new Set<SetupStepKey>(
    body.steps.filter((step) => step.done).map((step) => step.key),
  );
  const steps = coreSteps(body.preferences.focusAreas, applicable);
  return { done: steps.filter((key) => doneKeys.has(key)).length, total: steps.length, steps };
}

export interface SetupNavItemProps {
  /** `sidebar` is the desktop rail; `drawer` is the phone and tablet menu (bigger touch target). */
  variant?: 'sidebar' | 'drawer';
  /** Called when the link is followed, so the phone menu can close. */
  onNavigate?: () => void;
}

/**
 * "Setup 3/6" pinned at the top of the menu while the company's core setup steps are not all done,
 * with a thin bar for the same count. Only for people who can change the organisation's settings;
 * hidden while loading, on an error, and once every core step is done (Home's panel says so then).
 */
export function SetupNavItem({ variant = 'sidebar', onNavigate }: SetupNavItemProps) {
  const progress = useCoreSetupProgress();
  if (!progress || progress.total === 0 || progress.done >= progress.total) return null;

  const { done, total } = progress;
  const width = `${Math.round((done / total) * 100)}%`;
  const drawer = variant === 'drawer';

  return (
    <NavLink
      to="/setup"
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex w-full flex-col gap-1.5 rounded-lg border border-sidebar-border font-medium transition-colors',
          drawer ? 'min-h-11 px-2.5 py-2 text-sm' : 'px-2 py-1.5 text-xs',
          isActive
            ? 'bg-sidebar-active-bg font-semibold text-sidebar-active-fg'
            : 'bg-sidebar-hover-bg text-sidebar-fg hover:bg-sidebar-active-bg',
        )
      }
    >
      <span className={cn('flex items-center', drawer ? 'gap-3' : 'gap-2.5')}>
        <ListChecks className="size-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">Setup</span>
        <span className="shrink-0 tabular-nums text-sidebar-fg-muted" aria-hidden="true">
          {done}/{total}
        </span>
        <span className="sr-only">
          , {done} of {total} steps done
        </span>
      </span>
      <span
        className="block h-1 w-full overflow-hidden rounded-full bg-sidebar-hover-bg"
        aria-hidden="true"
      >
        <span className="block h-full rounded-full bg-sidebar-fg" style={{ width }} />
      </span>
    </NavLink>
  );
}
